import React from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MuiSelect from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import type { SelectProps as MuiSelectProps } from "@mui/material/Select";
import type { AutocompleteProps } from "@mui/material/Autocomplete";
import type { TextFieldProps } from "@mui/material/TextField";
import type { BaseMoleculeProps } from "../../types";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  group?: string;
}

export interface SelectProps extends BaseMoleculeProps {
  /**
   * Select options
   */
  options: SelectOption[];

  /**
   * Current value(s)
   */
  value?: string | number | (string | number)[];

  /**
   * Change handler
   */
  onChange?: (value: string | number | (string | number)[]) => void;

  /**
   * Input label
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Error state
   */
  error?: boolean;

  /**
   * Error message
   */
  errorMessage?: string;

  /**
   * Required field
   */
  required?: boolean;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Multiple selection
   */
  multiple?: boolean;

  /**
   * Enable search/autocomplete
   */
  searchable?: boolean;

  /**
   * Allow free text input (with autocomplete)
   */
  freeSolo?: boolean;

  /**
   * Full width
   */
  fullWidth?: boolean;

  /**
   * Size variant
   */
  size?: "small" | "medium";

  /**
   * Custom render option function
   */
  renderOption?: (option: SelectOption) => React.ReactNode;

  /**
   * Custom render value function (for multiple)
   */
  renderValue?: (selected: (string | number)[]) => React.ReactNode;

  /**
   * Additional props for underlying components
   */
  selectProps?: Omit<MuiSelectProps, "value" | "onChange">;
  autocompleteProps?: Partial<
    AutocompleteProps<SelectOption, boolean, boolean, boolean>
  >;
  textFieldProps?: Omit<TextFieldProps, "value" | "onChange">;
}

const SelectComponent: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error = false,
  errorMessage,
  required = false,
  disabled = false,
  multiple = false,
  searchable = false,
  freeSolo = false,
  fullWidth = true,
  size = "medium",
  renderOption,
  renderValue,
  variant = "default",
  className,
  "data-testid": dataTestId,
  selectProps,
  autocompleteProps,
  textFieldProps,
}) => {
  const hasError = error || Boolean(errorMessage);
  const displayHelperText = errorMessage || helperText;

  // Group options if they have groups
  const groupedOptions = React.useMemo(() => {
    const groups: Record<string, SelectOption[]> = {};
    const ungrouped: SelectOption[] = [];

    options.forEach((option) => {
      if (option.group) {
        if (!groups[option.group]) {
          groups[option.group] = [];
        }
        groups[option.group].push(option);
      } else {
        ungrouped.push(option);
      }
    });

    return { groups, ungrouped };
  }, [options]);

  // Handle value change
  const handleChange = React.useCallback(
    (newValue: any) => {
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  // Render searchable/autocomplete version
  if (searchable || freeSolo) {
    const selectedOptions = multiple
      ? options.filter(
          (opt) => Array.isArray(value) && value.includes(opt.value)
        )
      : options.find((opt) => opt.value === value);

    return (
      <Box className={className} data-testid={dataTestId}>
        <Autocomplete
          multiple={multiple}
          options={options}
          value={selectedOptions || (multiple ? [] : null)}
          onChange={(_, newValue) => {
            if (multiple && Array.isArray(newValue)) {
              // Handle multiple selection
              const values = newValue.map((item) =>
                typeof item === "string" ? item : item.value
              );
              handleChange(values);
            } else if (!multiple && newValue && !Array.isArray(newValue)) {
              // Handle single selection
              const value =
                typeof newValue === "string" ? newValue : newValue.value;
              handleChange(value);
            } else if (!multiple && !newValue) {
              handleChange("");
            }
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.label
          }
          isOptionEqualToValue={(option, value) => {
            const optionValue =
              typeof option === "string" ? option : option.value;
            const compareValue =
              typeof value === "string" ? value : value.value;
            return optionValue === compareValue;
          }}
          disabled={disabled}
          freeSolo={freeSolo}
          fullWidth={fullWidth}
          size={size}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={hasError}
              helperText={displayHelperText}
              required={required}
              sx={{
                // Mobile-first responsive styling
                "& .MuiInputLabel-root": {
                  fontSize: {
                    xs: "0.875rem", // Mobile
                    md: "1rem", // Desktop
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: {
                    xs: "0.875rem", // Mobile
                    md: "1rem", // Desktop
                  },
                },
                // Variant-specific styling
                ...(variant === "compact" && {
                  "& .MuiInputBase-root": {
                    minHeight: "40px",
                  },
                }),
                ...(variant === "expanded" && {
                  "& .MuiInputBase-root": {
                    minHeight: "56px",
                  },
                }),
              }}
              {...textFieldProps}
            />
          )}
          renderOption={(props, option) => (
            <li
              {...props}
              key={typeof option === "string" ? option : option.value}
            >
              {typeof option === "string"
                ? option
                : renderOption
                  ? renderOption(option)
                  : option.label}
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const key = typeof option === "string" ? option : option.value;
              const label = typeof option === "string" ? option : option.label;
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={key}
                  label={label}
                  size="small"
                  sx={{
                    // Mobile-first responsive chip styling
                    fontSize: {
                      xs: "0.75rem", // Mobile
                      md: "0.875rem", // Desktop
                    },
                  }}
                />
              );
            })
          }
          {...autocompleteProps}
        />
      </Box>
    );
  }

  // Render standard select version
  return (
    <Box className={className} data-testid={dataTestId}>
      <FormControl
        fullWidth={fullWidth}
        error={hasError}
        required={required}
        disabled={disabled}
        size={size}
        sx={{
          // Variant-specific styling
          ...(variant === "compact" && {
            "& .MuiInputBase-root": {
              minHeight: "40px",
            },
          }),
          ...(variant === "expanded" && {
            "& .MuiInputBase-root": {
              minHeight: "56px",
            },
          }),
        }}
      >
        {label && (
          <InputLabel
            sx={{
              // Mobile-first responsive label
              fontSize: {
                xs: "0.875rem", // Mobile
                md: "1rem", // Desktop
              },
            }}
          >
            {label}
          </InputLabel>
        )}

        <MuiSelect
          value={value || (multiple ? [] : "")}
          onChange={(event) => handleChange(event.target.value)}
          multiple={multiple}
          displayEmpty={Boolean(placeholder)}
          renderValue={
            multiple && renderValue
              ? (selected) => renderValue(selected as (string | number)[])
              : multiple
                ? (selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as (string | number)[]).map((val) => {
                        const option = options.find((opt) => opt.value === val);
                        return (
                          <Chip
                            key={val}
                            label={option?.label || val}
                            size="small"
                            sx={{
                              // Mobile-first responsive chip styling
                              fontSize: {
                                xs: "0.75rem", // Mobile
                                md: "0.875rem", // Desktop
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  )
                : undefined
          }
          sx={{
            // Mobile-first responsive styling
            "& .MuiSelect-select": {
              fontSize: {
                xs: "0.875rem", // Mobile
                md: "1rem", // Desktop
              },
            },
          }}
          {...selectProps}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              <em>{placeholder}</em>
            </MenuItem>
          )}

          {/* Render ungrouped options first */}
          {groupedOptions.ungrouped.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {renderOption ? renderOption(option) : option.label}
            </MenuItem>
          ))}

          {/* Render grouped options */}
          {Object.entries(groupedOptions.groups).map(
            ([groupName, groupOptions]) => [
              <MenuItem key={`group-${groupName}`} disabled>
                <strong>{groupName}</strong>
              </MenuItem>,
              ...groupOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  sx={{ paddingLeft: 4 }}
                >
                  {renderOption ? renderOption(option) : option.label}
                </MenuItem>
              )),
            ]
          )}
        </MuiSelect>

        {displayHelperText && (
          <FormHelperText
            sx={{
              // Mobile-first responsive helper text
              fontSize: {
                xs: "0.75rem", // Mobile
                md: "0.875rem", // Desktop
              },
            }}
          >
            {displayHelperText}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

// Apply memoization with React.memo
export const Select = React.memo(SelectComponent);

// Set display name for debugging
(Select as any).displayName = "Select";
